import React, { useState } from 'react'
import { format, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, addMonths, subMonths, isSameMonth, isSameDay, isWithinInterval, addDays } from 'date-fns'
import { ar, enUS } from 'date-fns/locale'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface RangeCalendarProps {
  startDate: Date | null
  endDate: Date | null
  onDateSelect: (date: Date) => void
  focusedMonth?: Date
  onMonthChange?: (date: Date) => void
  isRTL?: boolean
}

const RangeCalendar: React.FC<RangeCalendarProps> = ({
  startDate,
  endDate,
  onDateSelect,
  focusedMonth = new Date(),
  onMonthChange,
  isRTL = false,
}) => {
  const { i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? ar : enUS
  const [leftMonth, setLeftMonth] = useState(focusedMonth)
  const [rightMonth, setRightMonth] = useState(addMonths(focusedMonth, 1))

  const weekDays = isRTL 
    ? ['س', 'ح', 'ن', 'ث', 'ر', 'خ', 'ج']
    : ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

  const navigateMonth = (direction: 'prev' | 'next', side: 'left' | 'right') => {
    if (side === 'left') {
      const newMonth = direction === 'prev' ? subMonths(leftMonth, 1) : addMonths(leftMonth, 1)
      setLeftMonth(newMonth)
      if (onMonthChange) onMonthChange(newMonth)
    } else {
      const newMonth = direction === 'prev' ? subMonths(rightMonth, 1) : addMonths(rightMonth, 1)
      setRightMonth(newMonth)
      if (onMonthChange) onMonthChange(newMonth)
    }
  }

  const getDaysInMonth = (month: Date) => {
    const monthStart = startOfMonth(month)
    const monthEnd = endOfMonth(month)
    const calendarStart = startOfWeek(monthStart, { locale })
    const calendarEnd = endOfWeek(monthEnd, { locale })
    
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd })
  }

  const isDateInRange = (date: Date): boolean => {
    if (!startDate || !endDate) return false
    return isWithinInterval(date, { start: startDate, end: endDate })
  }

  const isDateStart = (date: Date): boolean => {
    return startDate ? isSameDay(date, startDate) : false
  }

  const isDateEnd = (date: Date): boolean => {
    return endDate ? isSameDay(date, endDate) : false
  }

  const isDateSelected = (date: Date): boolean => {
    return isDateStart(date) || isDateEnd(date)
  }

  const renderCalendar = (month: Date, side: 'left' | 'right') => {
    const days = getDaysInMonth(month)
    const monthName = format(month, 'MMMM yyyy', { locale })

    return (
      <div className="flex flex-col gap-3 flex-1 min-w-0">
        {/* Month Header */}
        <div className="flex items-center justify-between mb-4 px-2">
          <button
            type="button"
            onClick={() => navigateMonth('prev', side)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h3 className="text-sm font-semibold text-gray-900 font-['IBM Plex Sans Arabic']">
            {monthName}
          </h3>
          <button
            type="button"
            onClick={() => navigateMonth('next', side)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Next month"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Week Days Header */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day, index) => (
            <div
              key={index}
              className="text-xs font-medium text-gray-500 text-center py-2 font-['IBM Plex Sans Arabic']"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            const isCurrentMonth = isSameMonth(day, month)
            const isStart = isDateStart(day)
            const isEnd = isDateEnd(day)
            const inRange = isDateInRange(day)
            const isSelected = isDateSelected(day)

            return (
              <button
                key={index}
                type="button"
                onClick={() => onDateSelect(day)}
                disabled={!isCurrentMonth}
                className={`
                  relative h-9 w-9 text-sm rounded-lg transition-all
                  font-['IBM Plex Sans Arabic']
                  ${!isCurrentMonth ? 'text-gray-300 cursor-not-allowed' : 'text-gray-900 hover:bg-gray-100'}
                  ${isStart || isEnd 
                    ? 'bg-flutter-button text-white font-semibold hover:bg-flutter-button' 
                    : inRange 
                      ? 'bg-flutter-button/10 text-gray-900' 
                      : ''
                  }
                  ${isStart && inRange ? 'rounded-l-lg rounded-r-none' : ''}
                  ${isEnd && inRange ? 'rounded-r-lg rounded-l-none' : ''}
                `}
              >
                {format(day, 'd')}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 p-4 sm:flex-row">
      {renderCalendar(leftMonth, 'left')}
      {renderCalendar(rightMonth, 'right')}
    </div>
  )
}

export default RangeCalendar

